/**
 * Display Format Types
 * Defines how values should be formatted for display in spreadsheet cells
 */

export type DisplayFormatType = 'currency' | 'percentage' | 'number' | 'date' | 'text' | 'boolean' | 'custom';

export interface CurrencyDisplayFormat {
  type: 'currency';
  currency?: 'USD' | 'EUR' | 'GBP' | string;
  decimals?: number;
  compact?: boolean; // e.g., $1.2M instead of $1,200,000
}

export interface PercentageDisplayFormat {
  type: 'percentage';
  decimals?: number;
  multiplier?: number; // 100 for 0.5 -> 50%, 1 for 0.5 -> 0.5%
}

export interface NumberDisplayFormat {
  type: 'number';
  decimals?: number;
  thousandsSeparator?: boolean;
  compact?: boolean;
}

export interface DateDisplayFormat {
  type: 'date';
  format?: 'short' | 'long' | 'iso' | string; // 'MM/DD/YYYY', 'MMMM D, YYYY', etc.
}

export interface TextDisplayFormat {
  type: 'text';
}

export interface BooleanDisplayFormat {
  type: 'boolean';
  trueLabel?: string;
  falseLabel?: string;
}

export interface CustomDisplayFormat {
  type: 'custom';
  formatterRef: string; // Reference to a registered formatter function
}

export type DisplayFormat =
  | CurrencyDisplayFormat
  | PercentageDisplayFormat
  | NumberDisplayFormat
  | DateDisplayFormat
  | TextDisplayFormat
  | BooleanDisplayFormat
  | CustomDisplayFormat;

// Default display formats for common use cases
export const DEFAULT_DISPLAY_FORMATS: Record<string, DisplayFormat> = {
  currency: { type: 'currency', currency: 'USD', decimals: 2 },
  currencyCompact: { type: 'currency', currency: 'USD', decimals: 2, compact: true },
  percentage: { type: 'percentage', decimals: 2, multiplier: 100 },
  percentageDecimal: { type: 'percentage', decimals: 2, multiplier: 1 },
  number: { type: 'number', decimals: 2, thousandsSeparator: true },
  integer: { type: 'number', decimals: 0, thousandsSeparator: true },
  moic: { type: 'number', decimals: 2, thousandsSeparator: false },
  dateShort: { type: 'date', format: 'short' },
  dateLong: { type: 'date', format: 'long' },
  text: { type: 'text' },
  boolean: { type: 'boolean', trueLabel: 'Yes', falseLabel: 'No' },
  shares: { type: 'number', decimals: 0, thousandsSeparator: true },
  multiple: { type: 'number', decimals: 2, thousandsSeparator: false },
};
